import { PERMISSIONS, type AuthenticatedUser, type PermissionCode } from '@solar/contracts';

export type StaffDashboardVariant = 'overview' | 'sales' | 'finance' | 'support';

export const STAFF_DASHBOARD_VARIANT_LABELS: Readonly<Record<StaffDashboardVariant, string>> = {
  overview: 'Overview',
  sales: 'Sales',
  finance: 'Finance',
  support: 'Support',
};

/** Whether the signed-in user should see the staff operational dashboard at `/app`. */
export function shouldShowStaffDashboard(
  user: AuthenticatedUser | null,
  can: (permission: PermissionCode) => boolean,
): boolean {
  if (user === null) {
    return false;
  }

  const hasStaffDashboardPermission =
    can(PERMISSIONS.REPORTS_READ) ||
    can(PERMISSIONS.USERS_READ) ||
    can(PERMISSIONS.NOTIFICATIONS_READ);
  const isPureCustomer = user.userType === 'CUSTOMER' && !hasStaffDashboardPermission;

  return hasStaffDashboardPermission && !isPureCustomer;
}

/** Dashboard API variants the user may request. */
export function getAccessibleDashboardVariants(
  can: (permission: PermissionCode) => boolean,
): StaffDashboardVariant[] {
  const variants: StaffDashboardVariant[] = [];

  if (can(PERMISSIONS.REPORTS_READ)) {
    variants.push('overview', 'sales', 'finance');
  }

  if (can(PERMISSIONS.NOTIFICATIONS_READ)) {
    variants.push('support');
  }

  return variants;
}

export function canAccessDashboardVariant(
  variant: StaffDashboardVariant,
  can: (permission: PermissionCode) => boolean,
): boolean {
  if (variant === 'support') {
    return can(PERMISSIONS.NOTIFICATIONS_READ);
  }

  return can(PERMISSIONS.REPORTS_READ);
}

/**
 * Pick the default staff dashboard variant from permissions.
 *
 * Super/system administrators land on overview when available; finance staff on
 * finance; sales on sales; support-only staff on support. Falls back to the
 * first accessible variant.
 */
export function resolveDefaultStaffDashboardVariant(
  can: (permission: PermissionCode) => boolean,
): StaffDashboardVariant | null {
  const accessible = getAccessibleDashboardVariants(can);
  if (accessible.length === 0) {
    return null;
  }

  if (can(PERMISSIONS.REPORTS_READ)) {
    if (can(PERMISSIONS.USERS_READ) && can(PERMISSIONS.ROLES_MANAGE)) {
      return 'overview';
    }

    if (can(PERMISSIONS.AUDIT_READ) && !can(PERMISSIONS.BILLING_READ)) {
      return 'overview';
    }

    if (can(PERMISSIONS.BILLING_READ) && !can(PERMISSIONS.SUBSCRIPTIONS_CREATE)) {
      return accessible.includes('finance') ? 'finance' : (accessible[0] ?? null);
    }

    if (can(PERMISSIONS.SUBSCRIPTIONS_CREATE) || can(PERMISSIONS.SUBSCRIPTIONS_UPDATE)) {
      return accessible.includes('sales') ? 'sales' : (accessible[0] ?? null);
    }

    return 'overview';
  }

  if (can(PERMISSIONS.NOTIFICATIONS_READ)) {
    return 'support';
  }

  return accessible[0] ?? null;
}

/** Whether the user should see customer self-service navigation groups. */
export function shouldShowCustomerNav(
  user: AuthenticatedUser | null,
  can: (permission: PermissionCode) => boolean,
): boolean {
  if (user === null || user.userType !== 'CUSTOMER') {
    return false;
  }

  return (
    can(PERMISSIONS.CUSTOMERS_READ) ||
    can(PERMISSIONS.SUBSCRIPTIONS_READ) ||
    can(PERMISSIONS.BILLING_READ) ||
    can(PERMISSIONS.PAYMENTS_AUTHORIZE_OWN)
  );
}

/** Whether the user should see staff operations navigation groups. */
export function shouldShowOperationsNav(
  user: AuthenticatedUser | null,
  can: (permission: PermissionCode) => boolean,
): boolean {
  if (user === null || user.userType === 'CUSTOMER') {
    return false;
  }

  return (
    can(PERMISSIONS.CUSTOMERS_READ) ||
    can(PERMISSIONS.SUBSCRIPTIONS_READ) ||
    can(PERMISSIONS.MANDATES_READ) ||
    can(PERMISSIONS.BILLING_READ) ||
    can(PERMISSIONS.PAYMENT_ATTEMPTS_READ) ||
    can(PERMISSIONS.PAYMENTS_READ) ||
    can(PERMISSIONS.REPORTS_READ) ||
    can(PERMISSIONS.NOTIFICATIONS_READ) ||
    can(PERMISSIONS.SYSTEM_HEALTH_READ)
  );
}
